import { useField, useFormikContext } from 'formik';
import { ChangeEvent, memo, useEffect, useRef } from 'react';
import { useKioskState } from '../../../../../context/Kiosk/kiosk';
import { useLocale } from '../../../../../context/Localization/localization';
import { BaseTextField } from '../../../BaseTextField/BaseTextField';
import { WebFormTextAreaProps } from '../FormTextArea.types';
import { useScrollToOnError } from '../../../../../helpers/useScrollTo';

export const WebFormTextArea = memo(
    ({
        placeholder,
        name,
        id,
        title,
        label,
        ariaLabel,
        disabled,
        required,
        autoFocus,
        capsLock,
        fullWidth,
        seventyFivePercentWidth,
        tabFocusRefContext,
    }: WebFormTextAreaProps) => {
        const [field, meta] = useField<string>(name);
        const { submitCount, setFieldValue } = useFormikContext();

        /**
         * A ref to the HTMLInputElement in the MUI TextField.
         */
        const inputRef = useRef<HTMLInputElement>(null);
        /**
         * The input value ref to prevent a stale state when the on-screen keyboard
         * calls any functions that use the internal value state.
         *
         * Probably not needed
         */
        const valueRef = useRef<string>(field.value ?? '');
        useScrollToOnError({
            scrollable: inputRef,
            name: name,
        });

        const locale = useLocale();
        const {
            dealer: { primaryLanguage },
        } = useKioskState();

        useEffect(() => {
            if (tabFocusRefContext) {
                tabFocusRefContext.current = inputRef.current;
            }
        }, [tabFocusRefContext]);

        const handleOnChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            valueRef.current = event.target.value;
            setFieldValue(name, event.target.value);
        };

        return (
            <BaseTextField
                name={name}
                id={id}
                title={title}
                label={label}
                ariaLabel={ariaLabel}
                inputMode={'text'}
                type={'text'}
                inputRef={inputRef}
                value={valueRef.current}
                error={meta.error}
                touched={meta.touched || submitCount > 0}
                disabled={disabled}
                required={required}
                autoFocus={autoFocus}
                fullWidth={fullWidth}
                seventyFivePercentWidth={seventyFivePercentWidth}
                capsLock={capsLock}
                onChange={handleOnChange}
                safariHack={locale === primaryLanguage ? 'primarylang' : 'alternatelang'}
                placeholder={placeholder}
                variant={'filled'}
                multiline={true}
                minRows={6}
                maxRows={6}
                sx={{
                    color: 'white',
                    '& .MuiInputBase-input::placeholder': {
                        color: 'black',
                        opacity: 0.6,
                    },
                    '& .MuiFilledInput-root': {
                        '&:before': {
                            borderBottom: '2px solid',
                        },
                        '&:hover:not(.Mui-disabled):before': {
                            borderBottom: '2px solid',
                        },
                        '&:after': {
                            borderBottom: '2px solid',
                        },
                    },
                }}
            />
        );
    },
);